import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FacebookIcon from '@mui/icons-material/Facebook';
import SendIcon from '@mui/icons-material/Send';
import SettingsIcon from '@mui/icons-material/Settings';
import CloseIcon from '@mui/icons-material/Close';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import IOSSwitch from 'src/components/switch/switch.component';
import HttpServices from 'src/services/httpService';
import { userModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import { addLiveChatSocketListener, connectLiveChatSocket } from 'src/utils/liveChatSocket';
import { useToaster } from 'src/utils/toaster/toasterContext';

const FACEBOOK_MESSAGE_PAGE_SIZE = 10;
const DEFAULT_SOCIAL_TIMEOUT_MESSAGE =
  'Our support team is not available right now. Please reply with your email address so an agent can create a support ticket and follow up with you by email.';
const EMAIL_PATTERN = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i;

const getInitialPageID = () => new URLSearchParams(window.location.search).get('page_id') || new URLSearchParams(window.location.search).get('account_id') || '';

const getMessagesData = (conversation) => conversation?.messages?.data || conversation?.messages || [];

const getMessageText = (message) => message?.text || message?.message || '';

const findCustomerEmail = (messages, pageID) => {
  for (const message of messages) {
    if (String(message?.from?.id || '') === String(pageID || '')) continue;
    const match = getMessageText(message).match(EMAIL_PATTERN);
    if (match) return match[0].toLowerCase();
  }
  return '';
};

const findLatestCustomerMessage = (messages, pageID) => {
  const message = messages.find(
    (item) => String(item?.from?.id || '') !== String(pageID || '') && getMessageText(item).trim()
  );
  return getMessageText(message).trim();
};

const getMessageImageURL = (message) => message?.image_url || message?.thumb_url || '';

const getMessageVideoURL = (message) => message?.video_url || '';

const getMessageAttachmentURL = (message) => message?.attachment_url || '';

const getAttachmentName = (message) => {
  if (message?.attachment_name) return message.attachment_name;
  const url = getMessageAttachmentURL(message);
  try {
    const parsed = new URL(url);
    const fileName = parsed.pathname.split('/').filter(Boolean).pop();
    return fileName || 'Attachment';
  } catch (_error) {
    return 'Attachment';
  }
};

const isSharedAttachment = (message) => ['share', 'link', 'fallback'].includes(String(message?.attachment_type || '').toLowerCase());

const getAttachmentDisplayURL = (message) => {
  const url = getMessageAttachmentURL(message);
  try {
    const parsed = new URL(url);
    return `${parsed.hostname}${parsed.pathname}`;
  } catch (_error) {
    return url;
  }
};

const getAttachmentLinkLabel = (message) => {
  if (isSharedAttachment(message)) return 'Open shared post/reel';
  return getAttachmentName(message);
};

const getSocketCreatedTime = (timestamp) => {
  const value = Number(timestamp || 0);
  return value > 0 ? new Date(value).toISOString() : new Date().toISOString();
};

const mergeMessages = (currentMessages, nextMessages) => {
  const seen = new Set();
  return [...currentMessages, ...nextMessages].filter((message) => {
    const id = message?.id || '';
    if (!id) return true;
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

const getConversationParticipant = (conversation, pageID) => {
  const participants = conversation?.participants?.data || conversation?.participants || [];
  return participants.find((participant) => participant.id !== pageID) || participants[0] || null;
};

export default function FacebookChatView() {
  const { showToast } = useToaster();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [selectedPageID, setSelectedPageID] = useState(getInitialPageID());
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagePagination, setMessagePagination] = useState('');
  const [reply, setReply] = useState('');
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [automationDialogOpen, setAutomationDialogOpen] = useState(false);
  const [automationLoader, setAutomationLoader] = useState(false);
  const [automationForm, setAutomationForm] = useState({
    auto_reply_enabled: false,
    timeout_minutes: 15,
    auto_reply_message: '',
  });
  const [automationTicketConfig, setAutomationTicketConfig] = useState({ configured: false, reason: '' });
  const [manualTicketOpen, setManualTicketOpen] = useState(false);
  const [manualTicketSaving, setManualTicketSaving] = useState(false);
  const [manualTicket, setManualTicket] = useState({
    requester_name: '',
    requester_email: '',
    subject: '',
    message: '',
  });
  const [supportTicketStatus, setSupportTicketStatus] = useState({
    can_create_ticket: false,
    ticket_creation_in_progress: false,
    timeout_message_sent: false,
    ticket: null,
  });
  const [supportTicketLoading, setSupportTicketLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesBodyRef = useRef(null);
  const showToastRef = useRef(showToast);
  const selectedConversationRef = useRef(null);
  const supportTicketRequestRef = useRef(0);

  const selectedAccount = useMemo(
    () => accounts.find((account) => String(account.id) === String(selectedPageID) || account.profile_id === selectedPageID) || null,
    [accounts, selectedPageID]
  );

  const selectedProviderPageID = selectedAccount?.profile_id || '';

  const selectedParticipant = useMemo(
    () => getConversationParticipant(selectedConversation, selectedProviderPageID),
    [selectedConversation, selectedProviderPageID]
  );

  const visibleMessages = useMemo(() => [...messages].reverse(), [messages]);

  const scrollToLatestMessage = useCallback(() => {
    requestAnimationFrame(() => messagesEndRef.current?.scrollIntoView({ block: 'end' }));
  }, []);

  const markAccountRead = useCallback((pageID = selectedPageID) => {
    if (!pageID) return;
    setAccounts((currentAccounts) =>
      currentAccounts.map((account) =>
        account.profile_id === pageID ? { ...account, unread_count: 0 } : account
      )
    );
    window.dispatchEvent(new CustomEvent('brand2chat:social-unread-refresh', { detail: { service: 'facebook' } }));
  }, [selectedPageID]);

  const fetchAccounts = useCallback((silent = false) => {
    if (!silent) setLoadingAccounts(true);
    HttpServices.Get({
      url: `${userModuleURL.connectedProfiles}?service_name=facebook`,
      successCallback: (data) => {
        const nextAccounts = (data?.accounts || []).filter((account) => account.is_active !== false && account.is_enable !== false);
        setAccounts(nextAccounts);
        setSelectedPageID((current) => {
          const selected = nextAccounts.find(
            (account) => String(account.id) === String(current) || String(account.profile_id) === String(current)
          );
          return String(selected?.profile_id || nextAccounts[0]?.profile_id || '');
        });
        if (!silent) setLoadingAccounts(false);
      },
      failureCallback: (message) => {
        if (!silent) setLoadingAccounts(false);
        showToastRef.current(message || 'Failed to fetch Facebook pages', 'e');
      },
    });
  }, []);

  const fetchConversations = useCallback((pageID = selectedPageID, silent = false) => {
    if (!pageID) {
      setConversations([]);
      setSelectedConversation(null);
      setMessages([]);
      return;
    }
    if (!silent) setLoadingConversations(true);
    const params = new URLSearchParams({ page_id: pageID });
    HttpServices.Get({
      url: `${userModuleURL.facebookConversations}?${params.toString()}`,
      successCallback: (data) => {
        const nextConversations = data?.conversations || [];
        setConversations(nextConversations);
        setSelectedConversation((current) => {
          const matchingConversation = current && nextConversations.find((conversation) => conversation.id === current.id);
          return matchingConversation || nextConversations[0] || null;
        });
        if (!silent) setLoadingConversations(false);
        markAccountRead(pageID);
      },
      failureCallback: (message) => {
        if (!silent) setLoadingConversations(false);
        showToastRef.current(message || 'Failed to fetch Facebook conversations', 'e');
      },
    });
  }, [markAccountRead, selectedPageID]);

  const fetchMessages = useCallback((conversationID, pageID = selectedPageID, silent = false, after = '', append = false) => {
    if (!conversationID || !pageID) {
      setMessages([]);
      setMessagePagination('');
      return;
    }
    if (append) setLoadingMoreMessages(true);
    else if (!silent) setLoadingMessages(true);
    const params = new URLSearchParams({ page_id: pageID, limit: String(FACEBOOK_MESSAGE_PAGE_SIZE) });
    if (after) params.set('after', after);
    HttpServices.Get({
      url: `${userModuleURL.facebookConversationMessages(conversationID)}?${params.toString()}`,
      successCallback: (data) => {
        const nextMessages = data?.messages || [];
        setMessages((currentMessages) => (append ? mergeMessages(currentMessages, nextMessages) : nextMessages));
        setMessagePagination(data?.pagination || '');
        if (append) setLoadingMoreMessages(false);
        else if (!silent) setLoadingMessages(false);
        if (!append) scrollToLatestMessage();
      },
      failureCallback: (message) => {
        if (append) setLoadingMoreMessages(false);
        else if (!silent) setLoadingMessages(false);
        showToastRef.current(message || 'Failed to fetch Facebook messages', 'e');
      },
    });
  }, [scrollToLatestMessage, selectedPageID]);

  const fetchSupportTicketStatus = useCallback((connectedProfileID, customerID, silent = false) => {
    const requestID = supportTicketRequestRef.current + 1;
    supportTicketRequestRef.current = requestID;
    if (!connectedProfileID || !customerID) {
      setSupportTicketStatus({ can_create_ticket: false, create_ticket_disabled_reason: '', ticket_creation_in_progress: false, timeout_message_sent: false, ticket: null });
      setSupportTicketLoading(false);
      return;
    }
    if (!silent) setSupportTicketLoading(true);
    const params = new URLSearchParams({
      service_name: 'facebook',
      connected_profile_id: String(connectedProfileID),
      customer_id: String(customerID),
    });
    HttpServices.Get({
      url: `${userModuleURL.socialSupportTicket}?${params.toString()}`,
      successCallback: (data) => {
        if (supportTicketRequestRef.current !== requestID) return;
        setSupportTicketStatus(data || { can_create_ticket: false, create_ticket_disabled_reason: '', ticket_creation_in_progress: false, timeout_message_sent: false, ticket: null });
        if (!silent) setSupportTicketLoading(false);
      },
      failureCallback: (message) => {
        if (supportTicketRequestRef.current !== requestID) return;
        setSupportTicketStatus({ can_create_ticket: false, create_ticket_disabled_reason: '', ticket_creation_in_progress: false, timeout_message_sent: false, ticket: null });
        if (!silent) {
          setSupportTicketLoading(false);
          showToastRef.current(message || 'Failed to fetch Facebook ticket status', 'e');
        }
      },
    });
  }, []);

  useEffect(() => {
    showToastRef.current = showToast;
  }, [showToast]);

  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  useEffect(() => {
    fetchConversations(selectedPageID);
  }, [fetchConversations, selectedPageID]);

  useEffect(() => {
    fetchMessages(selectedConversation?.id, selectedPageID);
  }, [fetchMessages, selectedConversation?.id, selectedPageID]);

  useEffect(() => {
    fetchSupportTicketStatus(selectedAccount?.id, selectedParticipant?.id);
  }, [fetchSupportTicketStatus, selectedAccount?.id, selectedParticipant?.id]);

  useEffect(() => {
    connectLiveChatSocket();
    const removeListener = addLiveChatSocketListener((data) => {
      if (data.type !== 'facebook:message:new') {
        return;
      }
      const payload = data.payload || {};
      const pageID = payload.page_id || '';
      fetchAccounts(true);
      if (pageID !== selectedAccount?.profile_id) {
        return;
      }
      const currentConversation = selectedConversationRef.current;
      const participant = getConversationParticipant(currentConversation, pageID);
      if (currentConversation?.id && payload.sender_id && participant?.id === payload.sender_id) {
        const attachmentURL = String(payload.attachment_url || '').trim();
        const attachmentType = String(payload.attachment_type || '').toLowerCase();
        const liveMessage = {
          id: payload.message_id || `live:${payload.sender_id}:${payload.timestamp || Date.now()}`,
          text: payload.text || '',
          created_time: getSocketCreatedTime(payload.timestamp),
          from: { id: payload.sender_id, name: participant?.name || 'Facebook customer' },
          to: { id: pageID, name: selectedAccount?.display_name || 'Facebook Page' },
          attachment_url: attachmentURL,
          attachment_type: attachmentType,
          image_url: attachmentType === 'image' ? attachmentURL : '',
          video_url: attachmentType === 'video' ? attachmentURL : '',
        };
        setMessages((currentMessages) => (currentMessages.some((message) => message.id === liveMessage.id) ? currentMessages : [liveMessage, ...currentMessages]));
        scrollToLatestMessage();
        fetchSupportTicketStatus(selectedAccount?.id, payload.sender_id, true);
      }
      fetchConversations(pageID, true);
      if (currentConversation?.id) {
        window.setTimeout(() => fetchMessages(currentConversation.id, pageID, true), payload.attachment_url ? 1200 : 0);
      }
    });
    return removeListener;
  }, [fetchAccounts, fetchConversations, fetchMessages, fetchSupportTicketStatus, scrollToLatestMessage, selectedAccount?.display_name, selectedAccount?.id, selectedAccount?.profile_id]);

  const handleMessagesScroll = useCallback((event) => {
    if (
      event.currentTarget.scrollTop <= 24 &&
      messagePagination &&
      !loadingMessages &&
      !loadingMoreMessages &&
      selectedConversation?.id
    ) {
      fetchMessages(selectedConversation.id, selectedPageID, true, messagePagination, true);
    }
  }, [fetchMessages, loadingMessages, loadingMoreMessages, messagePagination, selectedConversation?.id, selectedPageID]);

  const sendReply = () => {
    const body = reply.trim();
    const recipientID = selectedParticipant?.id || '';
    const pageID = selectedProviderPageID || selectedPageID;
    if (!body || !selectedAccount?.id || !pageID || !recipientID || sending) return;
    setSending(true);
    HttpServices.Post({
      url: userModuleURL.facebookMessages,
      body: {
        account_id: selectedAccount.id,
        page_id: pageID,
        conversation_id: selectedConversation?.id || '',
        recipient_id: recipientID,
        message: body,
      },
      successCallback: () => {
        setReply('');
        setSending(false);
        showToast('Facebook message sent', 's');
        fetchMessages(selectedConversation?.id, selectedPageID);
        fetchConversations(selectedPageID, true);
      },
      failureCallback: (message) => {
        setSending(false);
        showToast(message || 'Facebook message could not be sent', 'e');
      },
    });
  };

  const openManualTicket = () => {
    if (!selectedConversation || !selectedParticipant || !supportTicketStatus.can_create_ticket) return;
    const requesterName = selectedParticipant.name || 'Facebook customer';
    const requesterEmail = findCustomerEmail(messages, selectedProviderPageID);
    const customerMessage = findLatestCustomerMessage(messages, selectedProviderPageID);
    const messageContext = customerMessage ? `\n\nFacebook message:\n${customerMessage.slice(0, 600)}` : '';
    setManualTicket({
      requester_name: requesterName,
      requester_email: requesterEmail,
      subject: `Facebook support request - ${requesterName}`,
      message: `We created a support ticket for your Facebook conversation. Our support team will follow up with you by email.${messageContext}`,
    });
    setManualTicketOpen(true);
  };

  const closeManualTicket = () => {
    if (!manualTicketSaving) setManualTicketOpen(false);
  };

  const updateManualTicket = (field, value) => {
    setManualTicket((current) => ({ ...current, [field]: value }));
  };

  const createManualTicket = () => {
    const payload = {
      service_name: 'facebook',
      connected_profile_id: Number(selectedAccount?.id || 0),
      customer_id: selectedParticipant?.id || '',
      requester_name: manualTicket.requester_name.trim(),
      requester_email: manualTicket.requester_email.trim(),
      subject: manualTicket.subject.trim(),
      message: manualTicket.message.trim(),
    };
    if (!payload.requester_email || !EMAIL_PATTERN.test(payload.requester_email)) {
      showToast('Enter a valid customer email address', 'e');
      return;
    }
    if (!payload.subject || !payload.message) {
      showToast('Subject and message are required', 'e');
      return;
    }
    setManualTicketSaving(true);
    HttpServices.Post({
      url: userModuleURL.socialSupportTicket,
      body: payload,
      successCallback: (data) => {
        setManualTicketSaving(false);
        setManualTicketOpen(false);
        window.dispatchEvent(new CustomEvent('brand2chat:tickets-refresh'));
        const ticket = data?.ticket || null;
        setSupportTicketStatus((current) => ({ ...current, can_create_ticket: false, ticket_creation_in_progress: false, ticket }));
        const ticketNumber = ticket?.ticket_no ? ` ${ticket.ticket_no}` : '';
        showToast(data?.created === false ? `Ticket${ticketNumber} is already linked to this customer` : `Ticket${ticketNumber} created and emailed to the customer`, 's');
      },
      failureCallback: (message) => {
        setManualTicketSaving(false);
        fetchSupportTicketStatus(selectedAccount?.id, selectedParticipant?.id, true);
        showToast(message || 'Failed to create ticket', 'e');
      },
    });
  };

  const openAutomation = () => {
    if (!selectedAccount?.id) return;
    setAutomationDialogOpen(true);
    setAutomationLoader(true);
    HttpServices.Get({
      url: userModuleURL.connectedProfileAutomation('facebook', selectedAccount.id),
      successCallback: (data) => {
        setAutomationForm({
          auto_reply_enabled: Boolean(data?.auto_reply_enabled),
          timeout_minutes: Number(data?.timeout_minutes || 15),
          auto_reply_message: data?.auto_reply_message || DEFAULT_SOCIAL_TIMEOUT_MESSAGE,
        });
        setAutomationTicketConfig({
          configured: Boolean(data?.ticket_configured),
          reason: data?.ticket_config_reason || 'Complete Ticket Setup before enabling timeout messages.',
        });
        setAutomationLoader(false);
      },
      failureCallback: (message) => {
        setAutomationTicketConfig({ configured: false, reason: message || 'Ticket configuration could not be verified.' });
        setAutomationLoader(false);
        showToast(message || 'Failed to fetch Facebook automation settings', 'e');
      },
    });
  };

  const closeAutomation = () => {
    if (!automationLoader) setAutomationDialogOpen(false);
  };

  const saveAutomation = () => {
    if (!selectedAccount?.id) return;
    setAutomationLoader(true);
    HttpServices.Post({
      url: userModuleURL.connectedProfileAutomation('facebook', selectedAccount.id),
      body: {
        auto_reply_enabled: Boolean(automationForm.auto_reply_enabled),
        timeout_minutes: Number(automationForm.timeout_minutes || 15),
        auto_reply_message: automationForm.auto_reply_message || '',
      },
      successCallback: (_data, message) => {
        setAutomationLoader(false);
        setAutomationDialogOpen(false);
        showToast(message || 'Facebook automation settings saved successfully', 's');
      },
      failureCallback: (message) => {
        setAutomationLoader(false);
        showToast(message || 'Failed to save Facebook automation settings', 'e');
      },
    });
  };

  const formatTime = (value) => (value ? CommonHelper.formatDateAndTime(value) : '');

  return (
    <>
      <Box sx={{ p: { xs: 2, md: 3 }, height: 'calc(100vh - 96px)', minHeight: 620 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight={900}>
              Facebook Chat
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <FacebookIcon sx={{ color: '#1877F2' }} />
            <Select
              size="small"
              value={selectedPageID}
              displayEmpty
              onChange={(event) => {
                setSelectedPageID(event.target.value);
                setSelectedConversation(null);
                setMessages([]);
                setMessagePagination('');
              }}
              sx={{ minWidth: 260, bgcolor: 'background.paper' }}
            >
              <MenuItem value="" disabled>
                Select Facebook page
              </MenuItem>
              {accounts.map((account) => (
                <MenuItem key={account.id} value={String(account.profile_id)}>
                  {account.display_name || 'Facebook Page'}
                  {Number(account.unread_count || 0) > 0 ? ` (${account.unread_count} new)` : ''}
                </MenuItem>
              ))}
            </Select>
            <Tooltip title={selectedAccount ? 'Facebook timeout setup' : 'Select a Facebook page'}>
              <span>
                <Button
                  variant="outlined"
                  startIcon={<SettingsIcon />}
                  disabled={!selectedAccount}
                  onClick={openAutomation}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Setup
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Stack>

        <Grid container spacing={2} sx={{ height: 'calc(100% - 72px)', minHeight: 0 }}>
        <Grid item xs={12} md={4} lg={3} sx={{ height: '100%', minHeight: 0 }}>
          <Paper variant="outlined" sx={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 2 }}>
              <Typography fontWeight={800}>Conversations</Typography>
              {selectedAccount && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    {selectedAccount.display_name || selectedAccount.profile_id}
                  </Typography>
                  {Number(selectedAccount.unread_count || 0) > 0 && (
                    <Chip size="small" color="error" label={`${selectedAccount.unread_count} new`} />
                  )}
                </Stack>
              )}
            </Box>
            <Divider />
            {(loadingAccounts || loadingConversations) && (
              <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={22} />
              </Box>
            )}
            <List sx={{ overflowY: 'auto', minHeight: 0, flex: 1, p: 0 }}>
              {conversations.map((conversation) => {
                const participant = getConversationParticipant(conversation, selectedProviderPageID);
                const latestMessage = getMessagesData(conversation)[0] || {};
                const preview = getMessageText(latestMessage) || (getMessageImageURL(latestMessage) || getMessageVideoURL(latestMessage) || getMessageAttachmentURL(latestMessage) ? 'Attachment' : 'No message preview');
                return (
                  <ListItemButton
                    key={conversation.id}
                    selected={selectedConversation?.id === conversation.id}
                    onClick={() => setSelectedConversation(conversation)}
                    sx={{ alignItems: 'flex-start', py: 1.5 }}
                  >
                    <Avatar sx={{ mr: 1.5, bgcolor: '#dbeafe', color: '#1877F2' }}>
                      <FacebookIcon fontSize="small" />
                    </Avatar>
                    <ListItemText
                      primary={
                        <Typography fontWeight={800} noWrap>
                          {participant?.name || 'Facebook customer'}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography component="span" variant="caption" color="text.secondary" display="block" noWrap>
                            {participant?.id || conversation.id}
                          </Typography>
                          <Typography component="span" variant="caption" color="text.primary" display="block" noWrap>
                            {preview}
                          </Typography>
                        </>
                      }
                    />
                  </ListItemButton>
                );
              })}
              {!loadingConversations && conversations.length === 0 && (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography fontWeight={800}>No Facebook chats</Typography>
                </Box>
              )}
            </List>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8} lg={9} sx={{ height: '100%', minHeight: 0 }}>
          <Paper variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {selectedConversation ? (
              <>
                <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: '#f8fafc' }}>
                  <Avatar sx={{ bgcolor: '#dbeafe', color: '#1877F2' }}>
                    <FacebookIcon />
                  </Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="h6" fontWeight={900} noWrap>
                      {selectedParticipant?.name || 'Facebook customer'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {selectedAccount?.display_name || selectedPageID}
                    </Typography>
                  </Box>
                  <Tooltip
                    title={
                      supportTicketStatus.ticket
                        ? 'Open the ticket linked to this customer'
                        : supportTicketStatus.ticket_creation_in_progress
                          ? 'Ticket creation is in progress'
                          : supportTicketStatus.can_create_ticket
                          ? "Create a support ticket using the customer's email"
                          : supportTicketStatus.create_ticket_disabled_reason || 'Waiting for a new Facebook customer message'
                    }
                  >
                    <span>
                      <Button
                        variant={supportTicketStatus.ticket ? 'contained' : 'outlined'}
                        size="small"
                        startIcon={<ConfirmationNumberIcon />}
                        onClick={() => {
                          if (supportTicketStatus.ticket?.id) {
                            navigate(`/tickets?ticket_id=${supportTicketStatus.ticket.id}`);
                            return;
                          }
                          openManualTicket();
                        }}
                        disabled={loadingMessages || manualTicketSaving || supportTicketLoading || supportTicketStatus.ticket_creation_in_progress || (!supportTicketStatus.ticket && !supportTicketStatus.can_create_ticket)}
                        sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                      >
                        {supportTicketLoading
                          ? 'Checking...'
                          : supportTicketStatus.ticket_creation_in_progress
                            ? 'Creating...'
                          : supportTicketStatus.ticket
                            ? `Open Ticket ${supportTicketStatus.ticket.ticket_no || ''}`
                            : 'Create Ticket'}
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
                <Divider />
                <Box ref={messagesBodyRef} onScroll={handleMessagesScroll} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: { xs: 1.5, md: 3 }, bgcolor: '#f8fafc' }}>
                  {loadingMoreMessages && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                      <CircularProgress size={18} />
                    </Box>
                  )}
                  {loadingMessages && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={22} />
                    </Box>
                  )}
                  {visibleMessages.map((message) => {
                    const isAgent = message?.from?.id === selectedProviderPageID;
                    const messageText = getMessageText(message);
                    const imageURL = getMessageImageURL(message);
                    const videoURL = getMessageVideoURL(message);
                    const attachmentURL = getMessageAttachmentURL(message);
                    const showAttachmentLink = attachmentURL && (!imageURL || isSharedAttachment(message)) && (!videoURL || isSharedAttachment(message));
                    return (
                      <Box key={message.id} sx={{ display: 'flex', justifyContent: isAgent ? 'flex-end' : 'flex-start', mb: 1.5 }}>
                        <Card
                          variant="outlined"
                          sx={{
                            maxWidth: { xs: '88%', md: '68%' },
                            p: 1.5,
                            borderRadius: 1,
                            bgcolor: isAgent ? '#dbeafe' : '#fff',
                            borderColor: isAgent ? '#93c5fd' : 'divider',
                          }}
                        >
                          {messageText && (
                            <Typography sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {messageText}
                            </Typography>
                          )}
                          {imageURL && (
                            <Box
                              component="img"
                              src={imageURL}
                              alt="Facebook attachment"
                              sx={{ display: 'block', maxWidth: 280, width: '100%', borderRadius: 1, mt: messageText ? 1 : 0 }}
                            />
                          )}
                          {videoURL && (
                            <Box
                              component="video"
                              src={videoURL}
                              controls
                              sx={{ display: 'block', maxWidth: 280, width: '100%', borderRadius: 1, mt: messageText || imageURL ? 1 : 0 }}
                            />
                          )}
                          {showAttachmentLink && (
                            <Box
                              component="a"
                              href={attachmentURL}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                display: 'block',
                                mt: messageText ? 1 : 0,
                                p: 1,
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                color: 'primary.main',
                                textDecoration: 'none',
                                wordBreak: 'break-word',
                              }}
                            >
                              <Typography component="span" fontWeight={800} color="primary.main">
                                {getAttachmentLinkLabel(message)}
                              </Typography>
                              <Typography component="span" variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                {getAttachmentDisplayURL(message)}
                              </Typography>
                            </Box>
                          )}
                          {!messageText && !imageURL && !videoURL && !attachmentURL && (
                            <Typography color="text.secondary">Attachment</Typography>
                          )}
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'right', mt: 0.5 }}>
                            {formatTime(message.created_time)}
                          </Typography>
                        </Card>
                      </Box>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </Box>
                <Divider />
                <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
                  <Stack direction="row" spacing={1}>
                    <TextField
                      fullWidth
                      multiline
                      maxRows={4}
                      value={reply}
                      placeholder="Reply on Facebook..."
                      onChange={(event) => setReply(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' && !event.shiftKey) {
                          event.preventDefault();
                          sendReply();
                        }
                      }}
                    />
                    <IconButton color="primary" onClick={sendReply} disabled={sending || !reply.trim() || !selectedParticipant?.id}>
                      {sending ? <CircularProgress size={22} /> : <SendIcon />}
                    </IconButton>
                  </Stack>
                </Box>
              </>
            ) : (
              <Box sx={{ height: '100%', display: 'grid', placeItems: 'center', textAlign: 'center', p: 3 }}>
                <Box>
                  <FacebookIcon sx={{ fontSize: 54, color: '#1877F2', mb: 1 }} />
                  <Typography variant="h5" fontWeight={900}>
                    Select a Facebook chat
                  </Typography>
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>
        </Grid>
      </Box>

      <Dialog
        open={manualTicketOpen}
        onClose={closeManualTicket}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}
      >
        <DialogTitle sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Create support ticket
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              The first ticket message will be emailed to the customer.
            </Typography>
          </Box>
          <IconButton aria-label="Close create ticket" onClick={closeManualTicket} disabled={manualTicketSaving} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ px: 3, py: 2.5 }}>
          <Stack spacing={2}>
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Avatar sx={{ width: 38, height: 38, bgcolor: '#E7F0FF', color: '#1877F2' }}>
                <FacebookIcon fontSize="small" />
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography fontWeight={700} noWrap>
                  {selectedParticipant?.name || 'Facebook customer'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedAccount?.display_name || 'Facebook page'}
                </Typography>
              </Box>
            </Stack>

            <TextField
              label="Customer name"
              value={manualTicket.requester_name}
              onChange={(event) => updateManualTicket('requester_name', event.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Customer email"
              type="email"
              value={manualTicket.requester_email}
              onChange={(event) => updateManualTicket('requester_email', event.target.value)}
              helperText={manualTicket.requester_email ? 'Detected from the conversation. Confirm before creating the ticket.' : 'Enter the email shared by the customer in the conversation.'}
              fullWidth
              required
              size="small"
            />
            <TextField
              label="Subject"
              value={manualTicket.subject}
              onChange={(event) => updateManualTicket('subject', event.target.value)}
              fullWidth
              required
              size="small"
            />
            <TextField
              label="Email message"
              value={manualTicket.message}
              onChange={(event) => updateManualTicket('message', event.target.value)}
              fullWidth
              required
              multiline
              minRows={5}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
          <Button variant="outlined" onClick={closeManualTicket} disabled={manualTicketSaving} sx={{ minWidth: 88 }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={createManualTicket} disabled={manualTicketSaving} sx={{ minWidth: 112 }}>
            {manualTicketSaving ? <CircularProgress color="inherit" size={18} /> : 'Create Ticket'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={automationDialogOpen}
        onClose={closeAutomation}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}
      >
        <DialogTitle sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Facebook timeout setup
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              Configure the response sent when no agent replies.
            </Typography>
          </Box>
          <IconButton aria-label="Close timeout setup" onClick={closeAutomation} disabled={automationLoader} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ px: 3, py: 2.5 }}>
          <Stack spacing={2.25}>
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Avatar sx={{ width: 38, height: 38, bgcolor: '#E7F0FF', color: '#1877F2' }}>
                <FacebookIcon fontSize="small" />
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography fontWeight={700} noWrap>
                  {selectedAccount?.display_name || 'Facebook Page'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Facebook page
                </Typography>
              </Box>
            </Stack>

            {!automationTicketConfig.configured && !automationLoader && (
              <Alert
                severity="warning"
                action={
                  <Button color="inherit" size="small" onClick={() => navigate('/ticket-setup')}>
                    Ticket Setup
                  </Button>
                }
              >
                {automationTicketConfig.reason}
              </Alert>
            )}

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                p: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <Box>
                <Typography variant="body2" fontWeight={700}>
                  Timeout message
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Send once for each unanswered customer message until a ticket is created.
                </Typography>
              </Box>
              <IOSSwitch
                checked={automationForm.auto_reply_enabled}
                onChange={(event) => setAutomationForm((current) => ({ ...current, auto_reply_enabled: event.target.checked }))}
                disabled={automationLoader || !automationTicketConfig.configured}
                inputProps={{ 'aria-label': 'Enable Facebook timeout message' }}
              />
            </Box>

            <TextField
              type="number"
              label="Timeout minutes"
              size="small"
              fullWidth
              value={automationForm.timeout_minutes}
              inputProps={{ min: 1, max: 1440 }}
              helperText="Choose between 1 minute and 24 hours."
              disabled={automationLoader || !automationTicketConfig.configured}
              onChange={(event) => setAutomationForm((current) => ({ ...current, timeout_minutes: event.target.value }))}
            />
            <TextField
              label="Timeout message"
              multiline
              minRows={4}
              fullWidth
              value={automationForm.auto_reply_message}
              disabled={automationLoader || !automationTicketConfig.configured}
              onChange={(event) => setAutomationForm((current) => ({ ...current, auto_reply_message: event.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
          <Button variant="outlined" onClick={closeAutomation} disabled={automationLoader} sx={{ minWidth: 88 }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={saveAutomation} disabled={automationLoader || !automationTicketConfig.configured} sx={{ minWidth: 88 }}>
            {automationLoader ? <CircularProgress color="inherit" size={18} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}


